import { useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import { Models } from "@/api/types";
import { Text } from "@/atoms";
import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useConfetti, useCountUp } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  acknowledgeGiftCardTransferById,
  getNotAcknowledgedGiftCards,
  refreshNotAcknowledgedGiftCardsQuery,
} from "@/modules/giftCards";
import { RewardProgressBar } from "@/modules/onboarding/components/RewardProgressBar/RewardProgressBar";
import { get } from "api";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import OpenRewardXL from "atoms/icon/icons/ic_open_reward_XL_ol.svg";
import ClosedRewardXL from "atoms/icon/icons/ic_reward_XL_ol.svg";
import { AvailableRewardList } from "organisms/available-reward-list/AvailableRewardList";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import pluralize from "utils/pluralize";

import RepeatCash from "../../../atoms/icon/icons/RepeatCash.svg";

const load = async () => {
  const availableRewards = queryClient.fetchQuery(
    ["/rewards", { status: 1 }],
    () => get("/rewards", { status: 1 })
  );

  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const user = await queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const notAcknowledgedGiftCards = queryClient.fetchQuery(
    getNotAcknowledgedGiftCards.keyed()
  );

  return waitObj({
    availableRewards,
    platform,
    notAcknowledgedGiftCards,
    user,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const accountCreatedLoader: LoaderFunction = () => {
  return load();
};

export const AccountCreatedPage = () => {
  const navigate = useNavigate();
  const { availableRewards, platform, notAcknowledgedGiftCards, user } =
    useLoaderData() as LoaderData;
  const [rewardProgress, setRewardProgress] = useState(0);
  const progressBarValue = availableRewards.length > 0 ? 100 : 0;
  const [currentStep, setCurrentStep] = useState<"account-created" | "rewards">(
    "account-created"
  );

  const { triggerConfetti } = useConfetti();

  const handleNext = () => {
    if (currentStep === "account-created") {
      if (availableRewards.length > 0) {
        setCurrentStep("rewards");
      } else {
        navigate("/signin/friend-referral");
      }
    } else if (currentStep === "rewards") {
      navigate("/signin/friend-referral");
    }
  };

  useEffect(() => {
    if (currentStep === "rewards") {
      setTimeout(() => setRewardProgress(progressBarValue));
      setTimeout(() => {
        if (availableRewards.length > 0 && progressBarValue >= 100) {
          triggerConfetti();
        }
      }, 1500);
    } else if (currentStep === "account-created") {
      // Trigger confetti for account created (first step)
      triggerConfetti();
    }
  }, [currentStep, progressBarValue, availableRewards]);

  if (currentStep === "account-created") {
    return (
      <AccountCreatedStep
        giftCardTransfers={notAcknowledgedGiftCards}
        user={user}
        onNext={handleNext}
        title="Account created!"
        description={
          platform?.platformSettings?.rewardsEnabled
            ? "Tap next to get started and earn towards your first reward!"
            : "Tap next to get started."
        }
        icon={ClosedRewardXL}
      />
    );
  }

  if (currentStep === "rewards") {
    return (
      <RewardsStep
        availableRewards={availableRewards}
        rewardProgress={rewardProgress}
        progressBarValue={progressBarValue}
        onNext={handleNext}
      />
    );
  }

  return null;
};

const AccountCreatedStep = ({
  giftCardTransfers,
  user,
  icon,
  title,
  description,
  onNext,
}: {
  giftCardTransfers: Models<"DisplayGiftCardTransferItem">[];
  user: any;
  icon: any;
  title: string;
  description: string;
  onNext: () => void;
}) => {
  const giftCardSum =
    giftCardTransfers?.reduce(
      (acc, giftCard) => acc + giftCard.giftCardItemAmountInCents / 100,
      0
    ) || 0;

  const value = useCountUp({
    to: giftCardSum,
    from: 0,
    duration: 1500,
    delay: 250,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: async (transferIds: string[]) => {
      await Promise.all(
        transferIds.map((id) =>
          acknowledgeGiftCardTransferById({ transferId: id })
        )
      );
    },
    onSuccess: () => {
      refreshNotAcknowledgedGiftCardsQuery();
    },
  });

  useEffect(() => {
    if (giftCardTransfers.length > 0) {
      acknowledgeMutation.mutate(
        giftCardTransfers.map((transfer) => transfer.id)
      );
    }
  }, [giftCardTransfers]);

  const renderGiftCards = () => (
    <motion.div
      className="grid w-full gap-12 p-4 text-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: ANIMATION_DURATION, delay: 0.2 }}
    >
      <motion.div
        className="grid w-full gap-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.4 }}
      >
        <Text
          text={`Congrats ${user?.firstName}! You now have`}
          className="text-secondary"
        />

        <div className="flex items-center justify-center gap-2">
          <div className="">
            <RepeatCash height={48} width={48} />
          </div>
          <motion.div
            style={{
              width: Math.max(String(giftCardSum || 0).length * 20 + 20) + "px",
            }}
            className={cn("items-center justify-center")}
            initial={{ opacity: 0 }}
            animate={{ opacity: value === 0 ? 0 : 1 }}
            transition={{ duration: 0.25 }}
          >
            <Text
              text={`$${value || "-"}`}
              variant="h1"
              className="text-primary"
            />
          </motion.div>
        </div>
        <Text
          text="Available to use on any service."
          className="text-secondary"
        />
      </motion.div>
    </motion.div>
  );

  const renderDescription = () => (
    <motion.div
      className="text-center text-body1 text-secondary"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: ANIMATION_DURATION, delay: 0.4 }}
    >
      {description}
    </motion.div>
  );

  const hasGiftCards = giftCardTransfers.length > 0;

  return (
    <div className="flex h-dvh flex-col gap-12 bg-white pt-[40px]">
      <div className="flex flex-col items-center gap-7 p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        >
          <Icon svg={icon} size="xlarge" color="brandColor" />
        </motion.div>
        <motion.div
          className="font-serif text-h2 text-primary"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: ANIMATION_DURATION, delay: 0.2 }}
        >
          {title}
        </motion.div>
        {hasGiftCards ? renderGiftCards() : renderDescription()}

        <motion.div
          className="absolute inset-x-0 bottom-0 mx-auto p-4 pb-9 sm:w-[390px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: ANIMATION_DURATION,
            delay: hasGiftCards ? 2 : 1,
          }}
        >
          <Button fullWidth onClick={onNext}>
            Next
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

const RewardsStep = ({
  availableRewards,
  rewardProgress,
  progressBarValue,
  onNext,
}: {
  availableRewards: any[];
  rewardProgress: number;
  progressBarValue: number;
  onNext: () => void;
}) => {
  return (
    <div className="flex h-dvh flex-col bg-one pt-[40px]">
      <div className="flex flex-1 flex-col items-center gap-7 p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        >
          <Icon svg={OpenRewardXL} size="xlarge" color="brandColor" />
        </motion.div>
        <motion.div
          className="font-serif text-h2 text-primary"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: ANIMATION_DURATION, delay: 0.2 }}
        >
          {availableRewards.length}{" "}
          {pluralize(availableRewards.length, "reward", "rewards")} unlocked!
        </motion.div>
        <motion.div
          className="text-center text-body1 text-secondary"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: ANIMATION_DURATION, delay: 0.4 }}
        >
          Congratulations on unlocking your first reward! Tap next to get
          started.
        </motion.div>

        {!!availableRewards.length && (
          <>
            <motion.div
              className="w-full transition delay-1000"
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: ANIMATION_DURATION, delay: 0.6 }}
            >
              <RewardProgressBar
                progress={rewardProgress ?? progressBarValue}
              />
            </motion.div>
            <motion.div
              className="w-full"
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: ANIMATION_DURATION, delay: 0.8 }}
            >
              <AvailableRewardList
                rewards={availableRewards.map(
                  (
                    {
                      title,
                      repeatCashEnabled,
                      valueIfBanked,
                      termsAndConditions,
                    },
                    index
                  ) => ({
                    id: index.toString(),
                    title,
                    valueIfBanked,
                    repeatCashEnabled,
                    tagDescription: "reward unlocked!",
                    disclaimer: termsAndConditions,
                  })
                )}
                showTitle={false}
                delay={1000}
              />
            </motion.div>
          </>
        )}
      </div>
      <motion.div
        className="absolute inset-x-0 bottom-0 mx-auto p-4 pb-9 sm:w-[390px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: ANIMATION_DURATION, delay: 2 }}
      >
        <Button fullWidth onClick={onNext}>
          Next
        </Button>
      </motion.div>
    </div>
  );
};
